import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listHands } from '../api/hands';
import { API_ORIGIN } from '../api/client';
import { CertifiedBadge } from '../components/auth/LoginButton';
import Spinner from '../components/common/Spinner';

const PAGE_SIZE = 100;

export default function ExplorePage() {
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 入力から300msのデバウンスで検索クエリを更新
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchQuery(inputValue.trim());
      setPage(1);
    }, 300);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [inputValue]);

  const { data: hands, isLoading } = useQuery({
    queryKey: ['explore', searchQuery, page],
    queryFn: () => listHands({ page, page_size: 100, q: searchQuery || undefined }),
  });

  const hasNextPage = (hands?.length ?? 0) === PAGE_SIZE;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-4">
      <h1 className="text-xl font-bold text-gray-900">探す</h1>

      {/* 検索バー */}
      <div className="relative">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
        </svg>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="タイトルで検索..."
          className="w-full pl-9 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
        />
        {inputValue && (
          <button
            onClick={() => setInputValue('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>

      {/* 件数・状態表示 */}
      {!isLoading && searchQuery && (
        <p className="text-sm text-gray-500">
          「{searchQuery}」の検索結果
          {hands && hands.length > 0 ? `（${(page - 1) * PAGE_SIZE + 1}〜${(page - 1) * PAGE_SIZE + hands.length}件）` : '：0件'}
        </p>
      )}
      {!isLoading && !searchQuery && (
        <p className="text-sm text-gray-500">新着順</p>
      )}

      {/* 結果リスト */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : !hands || hands.length === 0 ? (
        <div className="text-center text-gray-400 py-12 border rounded-xl">
          {searchQuery ? `「${searchQuery}」に一致する局面が見つかりません` : 'まだ投稿がありません'}
        </div>
      ) : (
        <div className="space-y-3">
          {hands.map((hand) => {
            const imageUrl = hand.source_image_url
              ? hand.source_image_url.startsWith('/')
                ? `${API_ORIGIN}${hand.source_image_url}`
                : hand.source_image_url
              : null;

            return (
              <Link
                key={hand.id}
                to={`/hands/${hand.id}`}
                className="flex items-center gap-3 p-3 border rounded-xl bg-white hover:bg-gray-50 transition-colors"
              >
                {imageUrl ? (
                  <img src={imageUrl} alt="" className="w-16 h-12 object-cover rounded border shrink-0" />
                ) : (
                  <div className="w-16 h-12 bg-gray-100 rounded border shrink-0 flex items-center justify-center text-gray-400 text-xs">
                    画像なし
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-gray-900 truncate">
                    {hand.title ?? '無題の局面'}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400 flex-wrap">
                    {hand.user && (
                      <span className="flex items-center gap-1">
                        {hand.user.x_avatar_url && (
                          <img src={hand.user.x_avatar_url} alt="" className="w-4 h-4 rounded-full" />
                        )}
                        @{hand.user.x_username}
                        {hand.user.is_pro && <CertifiedBadge />}
                      </span>
                    )}
                    <span>·</span>
                    <span className="text-green-600 font-medium">{hand.vote_count}票</span>
                    {hand.favorite_count > 0 && <><span>·</span><span>★{hand.favorite_count}</span></>}
                    <span>·</span>
                    <span>{new Date(hand.created_at).toLocaleDateString('ja-JP')}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* ページネーション */}
      {(page > 1 || hasNextPage) && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1}
            className="px-4 py-2 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50"
          >
            ← 前のページ
          </button>
          <span className="text-sm text-gray-500">{page}ページ</span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasNextPage}
            className="px-4 py-2 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50"
          >
            次のページ →
          </button>
        </div>
      )}
    </div>
  );
}
